import InputCompanyType from '@components/inputs/input-company-type/input-company-type.component'
import InputDocumentCnpj from '@components/inputs/input-document-cnpj/input-document-cnpj.component'
import { Bank, Input, InputDate, InputStates, Loader, ObjectHelper } from '@hub/shared'
import FormReceiveBank from '@pages/payments-data/components/form-receive-bank/form-receive-bank.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './form-payment-nf.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  payload: Bank | null
  initialPayload: Bank | null
  canEdit?: {
    bank: boolean
    full: boolean
    pis: boolean
  }
  hideData: boolean
  onPayloadChange?: (payload: Bank | null) => any
  onValidate?: (valid: boolean) => any
}

export default ({ payload, initialPayload, canEdit, hideData, onPayloadChange, onValidate }: Props) => {
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const [t] = useTranslation('formPaymentNf')
  const [loading, setLoading] = useState(true)
  const [validReceiveBank, setValidReceiveBank] = useState(false)
  const [state, setState] = useState<Bank | null>(null)

  const fieldsPayload = useMemo(() => ['companyName', 'companyOpeningDate', 'companyLegalNature', 'companyUf', 'companyCity'], [])
  const fieldsCnpjResponse = useMemo(() => ['nome', 'abertura', 'natureza_juridica', 'uf', 'municipio'], [])

  const parseDateToISOString = useCallback((dateString: string) => {
    try {
      return new Date(dateString?.split('/')?.reverse()?.join())?.toISOString()
    } catch (error) {
      return ''
    }
  }, [])

  useEffect(() => {
    if (payload && !objectHelper.compareObjects(state, payload)) {
      payload.companyOpeningDate = parseDateToISOString(payload.companyOpeningDate)
      setState({ ...payload })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload])

  useEffect(() => {
    if (initialPayload?.paymentType === 'rpa' || (initialPayload?.paymentType === 'nf' && state?.companyDocument !== undefined)) {
      setLoading(false)
    }
  }, [initialPayload, state])

  const payloadChange = useCallback(
    (state: Bank | null) => {
      if (state) {
        setState({ ...state })
      }

      if (onPayloadChange && typeof onPayloadChange === 'function') {
        onPayloadChange(state)
      }
    },
    [onPayloadChange]
  )

  const handleChange = useCallback(
    (fieldName: string, fieldValue: any, cnpjData?: any) => {
      if (fieldName === 'companyDocument') {
        for (const requiredField in fieldsPayload) {
          const field = fieldsPayload[requiredField]
          const value = fieldsCnpjResponse[requiredField]
          ;(state as any)[field] = cnpjData[value] || ''
        }
      }

      if (fieldName === 'typeOfBusiness' && fieldValue !== 'MEI (Microempreendedor Individual)') {
        state!.isPersonAccount = 0
      }

      ;(state as any)[fieldName] = fieldValue

      payloadChange(state)
    },
    [fieldsPayload, fieldsCnpjResponse, state, payloadChange]
  )

  const cnpjDataValues = useCallback(() => {
    let values = {}
    for (const field of fieldsPayload) {
      ;(values as any)[field] = field === 'companyOpeningDate' ? state?.[field]?.length === 10 : !!(state as any)?.[field]
    }
    return values
  }, [fieldsPayload, state])

  useEffect(() => {
    if (onValidate && typeof onValidate === 'function') {
      onValidate(
        state?.companyDocument?.length === 14 &&
          Object.values(cnpjDataValues()).every((value) => value) &&
          state.typeOfBusiness &&
          validReceiveBank
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, cnpjDataValues, validReceiveBank])

  if (loading) {
    return (
      <div className="text-center my-5" style={{ width: '100%' }}>
        <Loader size="big" />
      </div>
    )
  }

  let formReceipt = null
  if (state?.typeOfBusiness) {
    formReceipt = (
      <FormReceiveBank
        payload={state}
        initialPayload={initialPayload}
        canEdit={canEdit?.bank || false}
        hideData={hideData}
        onPayloadChange={(value) => {
          payloadChange(value)
        }}
        onValidate={(value) => setValidReceiveBank(value)}
      />
    )
  }

  return (
    <div className={'form-payment-nf mt-5'}>
      <fieldset>
        <InputDocumentCnpj
          className="mb-3"
          name="cnpj-payment-data"
          id="cnpj-payment-data"
          value={state?.companyDocument}
          type={hideData ? 'password' : 'text'}
          onChange={(value) => handleChange('companyDocument', value?.cnpj, value?.cnpjData)}
          label={'CNPJ'}
          required
          requiredOnLoad
          readOnly={hideData || !canEdit?.full}
        />
        <Input
          className="mb-3"
          name="company-name-payment-data"
          id="company-name-payment-data"
          value={state?.companyName}
          type={hideData ? 'password' : 'text'}
          onChange={(value) => handleChange('companyName', value)}
          label={t('companyName')}
          required
          readOnly={hideData || !canEdit?.full}
        />
        <InputDate
          className="mb-3"
          name="company-opening-date-payment-data"
          id="company-opening-date-payment-data"
          value={state?.companyOpeningDate}
          onChange={(value) => handleChange('companyOpeningDate', value)}
          label={t('companyOpeningDate')}
          type="date"
          required
          maxDate={new Date().toDateString()}
          readOnly={hideData || !canEdit?.full}
        />
        <Input
          className="mb-3"
          name="legal-nature-payment-data"
          id="legal-nature-payment-data"
          value={state?.companyLegalNature}
          type={hideData ? 'password' : 'text'}
          onChange={(value) => handleChange('companyLegalNature', value)}
          label={t('companyLegalNature')}
          required
          readOnly={hideData || !canEdit?.full}
        />
        <InputStates
          className="mb-3"
          name="uf-payment-data"
          id="uf-payment-data"
          value={state?.companyUf}
          onChange={(value: any) => handleChange('companyUf', value)}
          label={t('companyUf')}
          placeholder={'-'}
          required
          readOnly={hideData || !canEdit?.full}
        />
        <Input
          className="mb-3"
          name="city-payment-data"
          id="city-payment-data"
          value={state?.companyCity}
          onChange={(value) => handleChange('companyCity', value)}
          label={t('companyCity')}
          required
          readOnly={hideData || !canEdit?.full}
        />
        <InputCompanyType
          className="mb-3"
          name="company-type-select"
          id="company-type-select"
          value={state?.typeOfBusiness}
          onChange={(value) => handleChange('typeOfBusiness', value)}
          label={t('typeOfBusiness')}
          required
          requiredOnLoad
          readOnly={hideData || !canEdit?.full}
        />
        {formReceipt}
      </fieldset>
    </div>
  )
}
